import {AfterViewInit, Component, Input, OnInit, ViewChild} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {AppService} from "../../app.service";
import {ApiRequestService} from "../../shared/api-request.service";
import {UtilsService} from "../../shared/utils.service";
import {tap} from "rxjs/operators";
import {
  NetworkedAccountsSelectorComponent
} from "../../shared/networked-accounts-selector/networked-accounts-selector.component";
import {ApiDataSource} from "../../utils/api-data-source";
import { faHardHat } from '@fortawesome/free-solid-svg-icons';
import {AssetModel} from "../../models/asset.model";
import {UserPublicProfileComponent} from "../../shared/user-public-profile/user-public-profile.component";
import {
  AssetsViewContractorsFilterComponent
} from "../assets-view-contractors-filter/assets-view-contractors-filter.component";

@Component({
  selector: 'app-assets-view-contractors',
  templateUrl: './assets-view-contractors.component.html',
  styleUrls: ['./assets-view-contractors.component.scss']
})
export class AssetsViewContractorsComponent implements OnInit, AfterViewInit {

  @Input('asset_id') asset_id: number;
  @Input('asset') asset: AssetModel;

  dataSource: AssetsViewContractorsDataSource = new AssetsViewContractorsDataSource(this.app, this.api);
  faHardHat = faHardHat;

  listColumns: string[] = [
    'select',
    'id',
    'name',
    'contact_person',
    'industry',
    'date_created',
    // 'date_created_UTC',
    'actions'
  ];

  // the paginator and sorter
  @ViewChild('paginator1') paginator1: MatPaginator;
  @ViewChild('paginator2') paginator2: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService
  ) { }

  ngOnInit() {
    this.dataSource.asset_id = this.asset_id;
    this.dataSource.getData();
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting takes place
    this.sort.sortChange.subscribe(() => {
      this.paginator1.pageIndex = 0;
      this.paginator2.pageIndex = 0;
    });

    const paginatorTap = tap((paginator) => {
      this.paginator1.pageIndex = paginator['pageIndex'];
      this.paginator1.pageSize = paginator['pageSize'];
      this.paginator2.pageIndex = paginator['pageIndex'];
      this.paginator2.pageSize = paginator['pageSize'];

      this.dataSource.limit = paginator['pageSize'];
      this.dataSource.offset = paginator['pageIndex'];
      this.dataSource.getData();
    });

    // Subscribe to the paginator tap events.
    this.paginator1.page.pipe(paginatorTap).subscribe();
    this.paginator2.page.pipe(paginatorTap).subscribe();

    // Subscribe to the sorter tap events.
    this.sort.sortChange.pipe(tap((sorter) => {
      this.dataSource.order_by = sorter['active'];
      this.dataSource.order = sorter['direction'];

      // Sort UTC dates based on its corresponding date column.
      if ( sorter['active'] == 'date_created_UTC' ) {
        this.dataSource.order_by = 'date_created';
      }

      this.dataSource.getData();
    })).subscribe();
  }

  /**
   * Display a component dialog for managing contractors.
   *
   * @return {void}
   */
  onManageContractors(): void {
    this.utils.showComponentDialog(
      NetworkedAccountsSelectorComponent,
      {
        selected: this.dataSource.allIds,
        multiple: true
      },
      {
        width: '1200px'
      },
      (results) => {
        if (typeof results != 'undefined') {
          // Set the newly selected ids.
          this.dataSource.allIds = results;
          // Send API request to sync contractors.
          this.api.makeRequest('put', `v2/assets/${this.asset_id}/contractors`, {
            ids: this.dataSource.allIds
          })
            .then(() => {
              // Refresh the list.
              this.dataSource.getData();
            })
            .catch((errorResponse) => {
              this.utils.handleAPIErrors(errorResponse);
            });
        }
      }
    );
  }

  /**
   * Unlinks the selected contractors.
   *
   * @return {void}
   */
  onUnlinkSelected(): void {
    this.utils.showModal(
      `Unlink Selected ${ this.utils.getLangTerm('contractors.plural', 'Contractors') }`,
      `Are you sure you want to unlink the selected ${ this.utils.getLangTerm('contractors.plural', 'Contractors') }?`,
      () => {
        this.unlink(this.dataSource.selection.selected.join(','), `${ this.utils.getLangTerm('contractors.plural', 'Contractors') } were unlinked`);
      });
  }

  /**
   * Unlinks the selected contractor.
   *
   * @param {number} id - The ID of the contractor to be unlinked.
   * @return {void}
   */
  onUnlink(id: number): void {
    this.utils.showModal(
      `Unlink Selected ${ this.utils.getLangTerm('contractors.singular', 'Contractor') }`,
      `Are you sure you want to unlink the selected ${ this.utils.getLangTerm('contractors.singular', 'Contractor') }?`,
      () => {
        this.unlink(id, `${ this.utils.getLangTerm('contractors.singular', 'Contractor') } was unlinked`);
      });
  }

  /**
   * Unlinks contractors with the specified IDs.
   *
   * @param {any} ids - The contractor IDs to be unlinked.
   * @param msg
   * @return {void}
   */
  private unlink(ids: any, msg: string): void {
    this.api.makeRequest('delete', `v2/assets/${this.asset_id}/contractors`, {},
      {
        contractor_ids: ids
      })
      .then(() => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Show a component dialog for filtering contractors.
   *
   * @return {void}
   */
  onFilter(): void {
    this.utils.showComponentDialog(
      AssetsViewContractorsFilterComponent,
      {
        industry: this.dataSource.industry
      },
      {
        width: '500px'
      },
      (results) => {
        if (typeof results !== 'undefined') {
          this.dataSource.industry = results.industry;
          this.dataSource.getData();
        }
      }
    );
  }

  /**
   * Open the user's public profile dialog.
   *
   * @param hash
   */
  onUserPublicView(hash: string): void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }
}

export class AssetsViewContractorsDataSource extends ApiDataSource {
  order_by = 'date_created';
  order = 'desc';
  asset_id: number;
  industry: string = '';

  getData(resetOffset: boolean = false) {
    this.makeRequest(`v2/assets/${this.asset_id}/contractors`, resetOffset, {
      industry: this.industry
    });
  }
}
